import { z } from 'zod';
import { deliveryEstimate } from '../crew/schema/deliveryEstimate.js';
import { CountryCode } from '../enums/countries.js';
import { enumToZodLiteralUnion, helperSchema, optionalize, } from '../zodExtensions.js';
import { CartChangeDetectionMethod, CheckoutPlusAction, CheckoutPlusInsertionStrategy, CheckoutPlusRefreshMethod, CorsoCartAttribute, CorsoLineItemProperty, } from './shared.js';
// ? these schemas are a "type-first" approach to zod schemas, due to the limitation of what can and can't be ran on Shopify extensions
// ---------- SHIPPING CODE -----------------------------------
export const corsoShippingCodeSchema = z.object({
    rateTitle: z.string().optional(),
    rateId: z.number().optional(),
    rateKind: z.union([z.literal('plus'), z.literal('non-plus')]),
    shippingDiscount: z
        .object({
        message: z.string().optional(),
        amount: z.number(),
        type: z.union([z.literal('percentage'), z.literal('fixedAmount')]),
        isCheckboxApplicationEnabled: z.boolean(),
    })
        .optional(),
    shipProtectUnitId: z.number().optional(),
    deliveryEstimate: deliveryEstimate.optional(),
    pricingDetail: z
        .object({
        shippingPrice: z.number(),
        plusPrice: z.number(),
        currency: z.string(),
    })
        .optional(),
});
// -------------- WIDGET CONFIG --------------------------------
const INSURANCE_TEXT_MAX = 128;
const modalSchema = z.object({
    imageUrl: z.string().url(),
    title: helperSchema.insuranceString(INSURANCE_TEXT_MAX),
    description: z.string().nullable(),
    benefits: z.array(z.object({
        title: helperSchema.insuranceString(INSURANCE_TEXT_MAX),
        description: helperSchema.insuranceString(),
    })),
});
export const shippingPlusConfigSchema = z.object({
    // -- General Settings
    isEnabled: z.boolean().optional().default(false),
    isRateTableCacheEnabled: z.boolean().optional().default(false),
    enabledLineItemProperties: z
        .array(enumToZodLiteralUnion(CorsoLineItemProperty))
        .optional(),
    isWidgetDebugEnabled: z.boolean().optional().default(false),
    shouldUseDiscountedPrice: z.boolean().optional().default(true),
    isFreeShippingOptInEnabled: z.boolean().optional().default(false),
    isUpdateExistingDiscountsEnabled: z.boolean().optional().default(false),
    backupRateIds: z.array(z.number()).optional().default([]),
    enabledCartAttributes: z
        .array(enumToZodLiteralUnion(CorsoCartAttribute))
        .optional()
        .default([]),
    // -- Delivery Customization Settings
    deliveryCustomization: z
        .object({
        isEnabled: z.boolean().optional().default(false),
        isReorderShippingLinesEnabled: z.boolean().optional().default(false),
        hideRateNames: z.array(z.string()).optional().default([]),
        hideRateNamesOptOut: z.array(z.string()).optional().default([]),
    })
        .optional(),
    intelligems: z
        .object({
        isEnabled: z.boolean().optional().default(false),
        lineItemAttributeKey: z.string().optional().default('_igTestGroup'),
    })
        .optional(),
    // -- UI settings for the Widget + Modal-------
    label: helperSchema.insuranceString(INSURANCE_TEXT_MAX),
    freeShippingLabel: helperSchema.nullifiedInsuranceString(INSURANCE_TEXT_MAX),
    freeShippingDescription: helperSchema.nullifiedInsuranceString(),
    description: helperSchema.nullifiedInsuranceString(),
});
const selectorArray = z.string().array().min(1);
const checkoutPlusInsertionStrategy = enumToZodLiteralUnion(CheckoutPlusInsertionStrategy);
const checkoutPlusElement = z.object({
    selectors: selectorArray,
    title: z.string(),
    description: helperSchema.nullifiedInsuranceString(128),
    insertionStrategy: checkoutPlusInsertionStrategy,
    customCss: z.string().default(''),
    imgUrl: optionalize(z.string().url()),
    imgHtmlContent: z.string().default(''),
    kind: z.union([z.literal('altCheckoutButton'), z.literal('checkoutInfo')]),
});
const checkoutPlusConfigSchema = z.object({
    isEnabled: z.boolean(),
    cartRetrievalKey: z.string().default('cart'),
    scriptTagNote: z.string().optional(),
    removeProtectionProductOnPageLoad: z.boolean().default(true),
    shouldProtectCompanyCarts: z.boolean().default(true),
    shouldQueryShadowDom: z.boolean().default(false),
    cartRefreshMethod: enumToZodLiteralUnion(CheckoutPlusRefreshMethod).default('reloadPage'),
    checkoutSelectors: selectorArray.default(['button[name="checkout"]']),
    checkoutButtonText: z.string().trim().optional(), // TODO consider rename to checkoutContent or checkoutTemplate
    areSubscriptionsEnabled: z.boolean().default(false),
    hideWidgetSkus: z.array(z.string()).default([]),
    isQueryAjaxCartApiEnabled: z.boolean().default(true),
    cartChangeDetectionMethod: enumToZodLiteralUnion(CartChangeDetectionMethod).default('cartWatcher'),
    displayCountryCodes: z
        .array(enumToZodLiteralUnion(CountryCode))
        .min(1)
        .default(['US']),
    // TODO keep in sync with defaults in `apps/corso-checkout-plus/src/main.ts` and `widgetDefaults.ts`
    cartSelectors: selectorArray.default(['cart-items', 'cart-drawer', '#cart']),
    checkoutAction: enumToZodLiteralUnion(CheckoutPlusAction),
    elements: z.array(checkoutPlusElement).refine((elements) => {
        const kinds = new Set(elements.map((e) => e.kind));
        return kinds.size === checkoutPlusElement.shape.kind.options.length; // all kinds must be present
    }, { message: 'At least one of each element type is required.' }),
});
export const widgetConfigSchema = z.object({
    bgres: z.array(z.string()).optional(),
    cces: z.array(z.string()).optional(),
    modal: modalSchema,
    uncheckOnExternalRemoval: z.boolean().optional(),
    externalStorefrontId: z.string().optional(),
    baseApiUrl: z.string().optional(),
    shippingPlus: shippingPlusConfigSchema.optional(),
    checkoutPlus: checkoutPlusConfigSchema.optional(),
});
export function decodeCorsoShippingCodeWithZod(code) {
    try {
        if (!code)
            return null;
        const parsed = corsoShippingCodeSchema.safeParse(JSON.parse(code));
        if (parsed.success)
            return parsed.data;
    }
    catch (error) {
        /* empty */
    }
    return null;
}
// ---------- CART LINE PROPERTIES (NEW APPROACH) -----------------------------
// TODO: enforce the types on this from the 'shared.ts` file
export const corsoLineItemPropertySchema = z.object({
    _corsoDeliveryGroupType: z.string().optional(),
    _corsoCustomer: z.string().optional(),
    _corsoDiscountedPrice: z.string().optional(),
});
//# sourceMappingURL=schema.js.map